import { createApi } from '@reduxjs/toolkit/query/react';
import {
  getEligibilityTransactionsByIdResType,
  getServiceAuthorizationByIDResType,
} from '@store/rtk/types';
import { idType } from '@src/types';
import { baseQuerySample } from '@store/helpers';
import {
  COVERAGE_AREA_TABLE_DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
} from '@src/utils/constants';

export const membersApi = createApi({
  reducerPath: 'Members',
  tagTypes: ['Members'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    createOrUpdateMemberInfo: builder.mutation({
      query: ({ body, isMemberSaved }) => ({
        url: `/v1/member/${isMemberSaved ? 'update' : 'create'}`,
        method: isMemberSaved ? 'PUT' : 'POST',
        body,
      }),
    }),
    getLimitations: builder.mutation({
      query: ({ body = {}, params = {} }) => ({
        url: `/v1/member/limits/member`,
        method: 'POST',
        body,
        params: {
          sortBy: 'expirationDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    createUpdateLimitations: builder.mutation({
      query: ({ data, id, method }) => ({
        url: `/v1/member/limits/${id ? `update/${id}` : 'create'}`,
        body: data,
        method: method,
      }),
    }),
    getLimitationById: builder.query({
      query: ({ id }) => ({
        url: `/v1/member/limits/limit/${id}`,
      }),
    }),
    getServiceAuthorizations: builder.mutation({
      query: ({ page, size, body = {}, params = {} }) => ({
        url: `/v1/member/authorization`,
        method: 'POST',
        body,
        params: {
          sortBy: 'expirationDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getServiceAuthorizationByID: builder.query<
      getServiceAuthorizationByIDResType,
      { id?: null | string }
    >({
      query: ({ id }) => ({
        url: `/v1/member/authorization/${id}`,
        method: 'GET',
      }),
    }),
    getEligibilityTransactions: builder.mutation({
      query: ({ page, size, body = {}, params = {} }) => ({
        url: `/v1/member/eligibility-histories`,
        method: 'POST',
        body,
        params: {
          sortBy: 'expirationDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getEligibilityTransactionsById: builder.query<
      getEligibilityTransactionsByIdResType,
      { id?: idType }
    >({
      query: ({ id }) => ({
        url: `/v1/member/eligibility-histories/${id}`,
        method: 'GET',
      }),
    }),
    getPreferences: builder.mutation({
      query: ({
        page = 0,
        size = 5,
        body = {},
        params = { sortBy: 'id', sortDir: 'desc', page, size },
      }) => ({
        url: `/v2/member/contractor-preferences/member`,
        method: 'POST',
        body,
        params,
      }),
    }),
    createOrUpdatePreferences: builder.mutation({
      query: ({ id, body }) => ({
        url: `/v2/member/contractor-preferences/${id ? `update/${id}` : 'create'}`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
    }),
    getPreferencesById: builder.query({
      query: ({ id }) => ({
        url: `/v2/member/contractor-preferences/${id}`,
        method: 'GET',
      }),
    }),
    getMemberDetails: builder.query({
      query: () => ({
        url: '/v1/member/member',
        method: 'GET',
      }),
    }),
    getMemberPronouns: builder.query({
      query: () => ({
        url: '/v1/member/info/pronouns',
      }),
    }),
    createUpdateServiceAuth: builder.mutation({
      query: ({ id, method, data }) => ({
        url: `/v1/member/authorization/${id ? `update/${id}` : 'create'}`,
        method: method,
        body: data,
      }),
    }),
    createUpdateTransactionsHistory: builder.mutation({
      query: ({ id, method, data }) => ({
        url: `/v1/member/eligibility-histories${id ? `/${id}` : '/create'}`,
        method: method,
        body: data,
      }),
    }),
    /*** Rides ***/

    getPreferenceAllTags: builder.query({
      query: () => ({
        url: `/v1/users/tags`,
      }),
    }),
    getPreferenceSelectedTags: builder.query({
      query: () => ({
        url: `/v1/users/tags/member`,
      }),
    }),
    getSearchedItems: builder.query({
      query: ({ query, params, type }) => ({
        url: `/v1/${type}/info/search?query=${query}`,
        params: {
          page: 0,
          size: 3,
          ...params,
        },
      }),
    }),
    addPreferenceTags: builder.mutation({
      query: ({ id }) => ({
        url: `/v1/users/tags/add/member`,
        method: 'POST',
        body: {
          tagId: id,
        },
      }),
    }),
    checkMember: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/member/check-member`,
        method: 'POST',
        body,
      }),
    }),
    createFromAllyMember: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/member/create-from-ally-office`,
        method: 'POST',
        body,
      }),
    }),
    createFromMemberManually: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/member/create-manually`,
        method: 'POST',
        body,
      }),
    }),
    checkEligibilityExpiration: builder.mutation({
      query: (body) => ({
        url: `/v1/member/eligibility-histories/check-expiration`,
        method: 'POST',
        body,
      }),
    }),
    checkAuthorizationNumber: builder.query({
      query: ({ losId, authNumber }) => ({
        url: `/v1/member/authorization/check/${losId}/${authNumber}`,
      }),
    }),
    isCheckAuthorizationNumber: builder.query<
      { exists: boolean },
      { authorizationNumber: string }
    >({
      query: ({ authorizationNumber }) => ({
        url: `/v1/member/authorization/exists/${authorizationNumber}`,
      }),
    }),
    checkMemberLimit: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/search/member/check-member-limit`,
        method: 'POST',
        body,
      }),
    }),
    searchMember: builder.mutation({
      query: ({ argument: { body = {} }, params = {} }) => ({
        url: `/v1/search/member/search-rides`,
        method: 'POST',
        body,
        params: {
          sortBy: 'fullName',
          sortDir: 'asc',
          page: 0,
          size: COVERAGE_AREA_TABLE_DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    searchForMember: builder.query({
      query: ({ query }) => ({
        url: `/v1/search/member/search?query=${query}`,
      }),
    }),
    searchForMemberByAddress: builder.mutation({
      query: ({ query }) => ({
        url: `/v1/search/location/search-by-address?query=${query}`,
      }),
    }),
    basicAnalytics: builder.query({
      query: ({ query }) => ({
        url: `/v1/search/member/basic-analytics?time-frame=${query}`,
      }),
    }),
    getRestrictedActive: builder.query({
      query: ({ params = {} }) => ({
        url: `/v2/member/contractor-preferences/restricted-active`,
        params,
      }),
    }),
  }),
});

export const {
  useBasicAnalyticsQuery,
  useLazyBasicAnalyticsQuery,
  useSearchMemberMutation,
  useSearchForMemberByAddressMutation,
  useCreateUpdateServiceAuthMutation,
  useCreateUpdateTransactionsHistoryMutation,
  useGetEligibilityTransactionsMutation,
  useLazyGetEligibilityTransactionsByIdQuery,
  useGetLimitationsMutation,
  useCreateUpdateLimitationsMutation,
  useCreateOrUpdateMemberInfoMutation,
  useGetServiceAuthorizationsMutation,
  useGetPreferencesMutation,
  useLazyGetMemberDetailsQuery,
  useLazyGetMemberPronounsQuery,
  useLazyGetLimitationByIdQuery,
  useLazyGetServiceAuthorizationByIDQuery,
  useLazyGetPreferenceAllTagsQuery,
  useLazyGetPreferenceSelectedTagsQuery,
  useAddPreferenceTagsMutation,
  useLazyGetSearchedItemsQuery,
  useCheckMemberMutation,
  useCreateFromAllyMemberMutation,
  useCreateFromMemberManuallyMutation,
  useCheckEligibilityExpirationMutation,
  useLazyCheckAuthorizationNumberQuery,
  useLazyIsCheckAuthorizationNumberQuery,
  useCheckMemberLimitMutation,
  useLazySearchForMemberQuery,
  useLazyGetPreferencesByIdQuery,
  useCreateOrUpdatePreferencesMutation,
  useLazyGetRestrictedActiveQuery,
} = membersApi;
